import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';

import ImageSkeleton from '../../components/Skeleton/ImageSkeleton';
import TextSkeleton from '../../components/Skeleton/TextSkeleton';

import GascoIcoRight from '../../assets/images/ico-right.svg';
import GascoIcoRightBlue from '../../assets/images/ico-right-blue.svg';
import GascoIcoClock from '../../assets/images/ico-clock.svg';
import GascoCaratulaPodcast from '../../assets/images/caratula.jpg';

// Services
import { getArticulos, getUltimosCasosExito } from '../../config/services/strapi/strapi';
import { Articulo, CasoExito } from '../../interfaces/types';

import { formatearFechaCorta, formatearFechaLarga } from '../../helpers/fecha';

function Articulos() {
  const [cargando, setCargando] = useState(true);
  const [primerArticulo, setPrimerArticulo] = useState<Articulo | null>(null);
  const [articulos, setArticulos] = useState<Articulo[]>([]);
  const [casosExito, setCasosExito] = useState<Partial<CasoExito>[]>([]);

  useEffect(() => {
    const fetchArticulos = async () => {
      setCargando(true);

      const rawArticulos = await getArticulos();
      const mappedArticulos: Articulo[] = rawArticulos.map((articulo: any) => ({
        id: articulo.id,
        titulo: articulo.titulo,
        fecha: articulo.fecha,
        portadaUrl: articulo.imagen_cubierta.url,
        minutosLectura: articulo.minutos_lectura,
        slug: articulo.slug,
        etiqueta: articulo.etiqueta,
      }));

      if (mappedArticulos.length) {
        const [first, ...rest] = mappedArticulos;
        setPrimerArticulo(first);
        setArticulos(rest || []);
      }

      setCargando(false);
    };

    const fetchCasosExito = async () => {
      const rawCasosExito = await getUltimosCasosExito();
      const mappedCasosExito: Partial<CasoExito>[] = rawCasosExito.map((caso: any) => ({
        id: caso.id,
        nombre: caso.nombre,
        slug: caso.slug,
      }));

      setCasosExito(mappedCasosExito);
    };

    fetchArticulos();
    fetchCasosExito();
  }, []);

  return (
    <main>
      <section className='relative flex justify-center items-center w-full h-[530px] md:h-[560px] xl:!h-[540px] 2xl:!h-[680px] bg-cover bg-center p-[16px] z-0' style={{ backgroundImage: `url(${primerArticulo?.portadaUrl})` }}>
        <div className="absolute inset-0 bg-black opacity-50 pointer-events-none z-10"></div>
        <div className="relative z-20 px-4 md:px-8 lg:px-36 2xl:px-72">
          <h1 className='!text-white text-[32px] md:text-[36px] lg:text-[48px] !font-[700] text-left leading-[34px] lg:leading-[48px]'>
            <NavLink className='!text-inherit' to="/articulos">{primerArticulo?.titulo}</NavLink>
          </h1>
          <small className="!text-white block pt-3 pb-6 !text-base">
            {primerArticulo ? formatearFechaCorta(primerArticulo.fecha) : ""}
          </small>
          <NavLink className='!text-white text-[15px] md:text-[18px] !font-[700] underline underline-offset-8 group' to={`/articulo/${primerArticulo?.slug}`}>
            <span>Ir a ver el artículo</span>
            <img src={GascoIcoRight} alt="Ir a ver el artículo" className='inline-block ml-4 transition group-hover:translate-x-[15px] duration-300' />
          </NavLink>
        </div>
      </section>
      <section className='px-6 md:px-10 lg:px-36 xl:px-48 2xl:px-72 pt-4 pb-6 mb-12 w-full'>
        <div className="flex justify-between md:justify-start items-center md:gap-8">
          <span className="rounded-lg !text-[var(--text-color-primary)] bg-[#dddddd] py-[5px] px-[22px] text-[16px] md:text-[18px] !font-[600]">
            {primerArticulo?.etiqueta || "Digitalización"}
          </span>
          <span className="flex justify-start items-center gap-4 pr-4">
            <img src={GascoIcoClock} alt="Tiempo de lectura" className='inline-block' />
            <span className='!text-[var(--text-color-primary)] text-[16px] !font-[600]'>{primerArticulo?.minutosLectura} min de lectura</span>
          </span>
        </div>
      </section>
      {/* Artículos */}
      <section className='px-4 md:px-10 lg:px-36 xl:px-48 2xl:px-72'>
        <div>
          <h2 className='uppercase !text-[var(--text-color-secondary)] !text-[24px] !font-[700]'>Otros Artículos</h2>
        </div>
        <div className="grid grid-cols-12 gap-4 md:gap-x-8 md:gap-y-16 pb-10">
          {
            cargando ?

              Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="col-span-12 md:col-span-6">
                  <ImageSkeleton className="w-full h-[200px] rounded-lg" />
                  <TextSkeleton className="mt-4 !max-w-lg" />
                </div>
              ))

              :

              articulos.map((articulo: Articulo) => (
                <article className='col-span-12 md:col-span-6'>
                  <NavLink className="pb-4" to={`/articulo/${articulo.slug}`}>
                    <img src={articulo.portadaUrl} alt="" />
                  </NavLink>
                  <div className="flex justify-between md:justify-start md:gap-4 items-center mt-4">
                    <span className="rounded-lg !text-[var(--text-color-primary)] bg-[#dddddd] py-[6px] px-[20px] text-[16px] !font-[600]">
                      {articulo.etiqueta || "Digitalización"}
                    </span>
                    <span className="flex justify-start items-center gap-4 md:gap-2 pr-4">
                      <img src={GascoIcoClock} alt="Tiempo de lectura" className='inline-block' />
                      <span className='!text-[var(--text-color-primary)] text-[16px] !font-[600] text-ellipsis overflow-hidden whitespace-nowrap'>{articulo.minutosLectura} min de lectura</span>
                    </span>
                  </div>
                  <NavLink className="!text-[var(--text-color-secondary)] text-[22px]" to={`/articulo/${articulo.slug}`}>
                    <h3 className='!font-[400] leading-7'>{articulo.titulo}</h3>
                  </NavLink>
                  <span className='text-[16px] block pt-4 pb-6'>{formatearFechaLarga(articulo.fecha, { monthAtStart: true })}</span>
                  <NavLink className='text-[20px] !font-[700] underline underline-offset-8' to={`/articulo/${articulo.slug}`}>
                    <span>Ir a ver el artículo</span>
                    <img src={GascoIcoRightBlue} alt="Ir a ver el artículo" className='inline-block ml-4 w-[32px]' />
                  </NavLink>
                </article>
              ))
          }
        </div>
      </section>
      {/* Interes */}
      <section className='px-4 md:px-10 lg:px-36 xl:px-48 2xl:px-72 mt-20 pb-8 md:pb-2'>
        <div className='pt-2 mb-8'>
          <hr className='!bg-[var(--background-color-tertiary)]' />
          <h2 className='!text-[var(--text-color-secondary)] !text-[24px] !font-[700] !uppercase'>También te puede interesar</h2>
        </div>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-8 rounded-xl p-[16px] !bg-[var(--background-color-secondary)] mb-10 md:mb-0">
            <h3 className="hidden md:block my-4 md:mt-0 text-[var(--text-color-secondary)] !text-[24px] !font-[600] leading-[28px]">Nuestros<br />casos de éxito</h3>
            {
              casosExito.map(caso => (
                <NavLink key={caso.id} className='block rounded-xl !text-[16px] !pl-[60px] !py-[8px] !pr-[20px] !bg-[#B1B1B140] hover:!bg-white transition mb-2' to={`/casos-de-exito/${caso.slug}`}>
                  <h3 className='!font-[400] leading-7 !bg-transparent 2xl:!text-[20px] 2xl:leading-[28px] !text-black'>{caso.nombre}</h3>
                </NavLink>
              ))
            }
            <NavLink className='block rounded-xl !text-black !text-[16px] !pl-[60px] !py-[8px] !pr-[20px] !bg-white mb-2 border !border-[var(--border-color-secondary)]' to="/casos-de-exito">
              <h3 className='!font-[400] leading-7 !bg-transparent'>Revisa nuestros casos de éxito</h3>
            </NavLink>
          </div>
          <div className="col-span-12 md:col-span-4 px-4 py-2 pb-4 rounded-xl !bg-[var(--background-color-secondary)] md:flex md:flex-col md:justify-center">
            <h2 className='!text-[var(--text-color-secondary)] !text-[24px] !font-[700] !uppercase !pt-0 !mt-0 pb-3'>Escucha nuestro podcast</h2>
            <NavLink className='text-[16px] !font-[700] underline underline-offset-8 group transition' to="/podcast">
              <span>Ir al podcast</span>
              <img src={GascoIcoRightBlue} alt="Ir a ver el artículo" className='inline-block ml-4 w-[32px] group-hover:translate-x-[15px] transition duration-300' />
            </NavLink>
            <img src={GascoCaratulaPodcast} alt="" className='mt-5 w-full h-auto rounded-sm' />
          </div>
        </div>
      </section>
    </main>
  )
}

export default Articulos;